import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";

/* ───────────────── FETCH BANNERS ───────────────── */
export const fetchBanners = createAsyncThunk(
  "banner/fetchBanners",
  async ({ page = 1, limit = 10, search = "" }, thunkAPI) => {
    try {

      /* Banners keep Node's wrapper: { status, message, data, pagination } */
      const response = await api.get(
        `/admin/content/banners?page=${page}&limit=${limit}&search=${search}`
      );

      return response.data;

    } catch (error) {

      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        "Failed to fetch banners"
      );

    }
  }
);

/* ───────────────── CREATE BANNER ───────────────── */
export const createBanner = createAsyncThunk(
  "banner/createBanner",
  async (payload, thunkAPI) => {
    try {

      /* JSON now — the server takes an uploads PATH for `image`, not a file */
      const response = await api.post(
        `/admin/content/banners`,
        payload
      );

      return response.data.data;

    } catch (error) {

      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        "Failed to create banner"
      );

    }
  }
);

/* ───────────────── UPDATE BANNER ───────────────── */
export const updateBanner = createAsyncThunk(
  "banner/updateBanner",
  async ({ id, payload }, thunkAPI) => {
    try {

      const response = await api.put(
        `/admin/content/banners/${id}`,
        payload
      );

      return response.data.data;

    } catch (error) {

      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        "Failed to update banner"
      );

    }
  }
);

/* ───────────────── DELETE BANNER ───────────────── */
export const deleteBanner = createAsyncThunk(
  "banner/deleteBanner",
  async (id, thunkAPI) => {
    try {

      await api.delete(
        `/admin/content/banners/${id}`
      );

      return id;

    } catch (error) {

      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        "Failed to delete banner"
      );

    }
  }
);

/* ───────────────── TOGGLE STATUS ───────────────── */
export const toggleBannerStatus = createAsyncThunk(
  "banner/toggleBannerStatus",
  async (id, thunkAPI) => {
    try {

      /* PATCH, not PUT: PUT on this path is the update route and an empty
         body there would blank the banner */
      const response = await api.patch(
        `/admin/content/banners/${id}/toggle`
      );

      return response.data.data;

    } catch (error) {

      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        "Failed to update status"
      );

    }
  }
);

/* ───────────────── SLICE ───────────────── */
const bannerSlice = createSlice({

  name: "banner",

  initialState: {

    banners: [],
    pagination: {},
    loading: false,
    error: null,

  },

  reducers: {},

  extraReducers: (builder) => {

    builder

      /* FETCH */
      .addCase(fetchBanners.pending, (state) => {

        state.loading = true;
        state.error = null;

      })

      .addCase(fetchBanners.fulfilled, (state, action) => {

        state.loading = false;

        state.banners =
          action.payload.data || [];

        state.pagination =
          action.payload.pagination || {};

      })

      .addCase(fetchBanners.rejected, (state, action) => {

        state.loading = false;
        state.error = action.payload;

      })

      /* CREATE */
      .addCase(createBanner.pending, (state) => {

        state.loading = true;

      })

      .addCase(createBanner.fulfilled, (state, action) => {

        state.loading = false;

        state.banners.unshift(action.payload);

      })

      .addCase(createBanner.rejected, (state, action) => {

        state.loading = false;
        state.error = action.payload;

      })

      /* UPDATE */
      .addCase(updateBanner.pending, (state) => {

        state.loading = true;

      })

      .addCase(updateBanner.fulfilled, (state, action) => {

        state.loading = false;

        const index = state.banners.findIndex(
          (item) => item.id === action.payload.id
        );

        if (index !== -1) {

          state.banners[index] =
            action.payload;

        }

      })

      .addCase(updateBanner.rejected, (state, action) => {

        state.loading = false;
        state.error = action.payload;

      })

      /* DELETE */
      .addCase(deleteBanner.pending, (state) => {

        state.loading = true;

      })

      .addCase(deleteBanner.fulfilled, (state, action) => {

        state.loading = false;

        state.banners =
          state.banners.filter(
            (item) => item.id !== action.payload
          );

      })

      .addCase(deleteBanner.rejected, (state, action) => {

        state.loading = false;
        state.error = action.payload;

      })

      /* TOGGLE STATUS */
      .addCase(toggleBannerStatus.fulfilled, (state, action) => {

        const index = state.banners.findIndex(
          (item) => item.id === action.payload.id
        );

        if (index !== -1) {

          state.banners[index] =
            action.payload;

        }

      });

  },

});

export default bannerSlice.reducer;